import { useState, useEffect, useCallback, useRef } from 'react';
import { Tab } from '@headlessui/react';
import {
  InformationCircleIcon,
  IdentificationIcon,
  HomeIcon,
  BeakerIcon,
  HeartIcon,
} from '@heroicons/react/24/outline';
import { getSpecies } from '../api/species';
import type { Species } from '../api/species';
import type { CreateBirdDto } from '../api/birds';
import { Gender, BirdStatus, GenderDeterminationMethod } from '../utils/enums';
import toast from 'react-hot-toast';
import BirdGenotypeEditor from './genetics/BirdGenotypeEditor';
import CageSelect from './CageSelect';
import type { GeneticsSummary, PendingGenotype } from '../utils/genotypeEditorUtils';
import SpeciesCombobox from './SpeciesCombobox';
import { getApiErrorMessage } from '../utils/errorHandling';
import TabbedFormActions, { isNonEmptyString } from './ui/TabbedFormActions';
import AmountInput from './ui/AmountInput';
import {
  BrandModal,
  BrandModalBody,
  BrandModalHeader,
} from './ui/BrandModal';
import { suppliersApi, type Supplier } from '../api/suppliers';
import { PaymentMethod, getPaymentMethodDisplay } from '../api/purchases';

interface AddBirdModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (bird: CreateBirdDto) => Promise<void>;
  /** Prefill from a certificate transfer import (buyer flow). */
  initialDraft?: CreateBirdDto | null;
}

function emptyFormData(): FormData {
  const today = new Date().toISOString().split('T')[0];
  return {
    bandId: '',
    speciesId: '',
    mutationName: '',
    gender: Gender.UNKNOWN,
    genderDeterminationMethod: GenderDeterminationMethod.NOT_SEXED,
    status: BirdStatus.AVAILABLE,
    sellPrice: '',
    hatchingDate: today,
    bandingDate: today,
    cageNumber: '',
    provenance: {
      type: 'BRED',
      fatherId: '',
      motherId: '',
      sellerName: '',
      sellerContact: '',
      sellerAddress: '',
      dateAcquired: today,
      price: '',
      notes: '',
      supplierId: '',
      paymentMethod: PaymentMethod.CASH,
      invoiceNumber: '',
      recordPurchase: true,
    },
    phenotype: '',
    genotype: '',
    notes: '',
  };
}

function draftToFormData(draft: CreateBirdDto): FormData {
  const dateOnly = (value?: string) => {
    if (!value) return new Date().toISOString().split('T')[0];
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return new Date().toISOString().split('T')[0];
    return d.toISOString().split('T')[0];
  };
  return {
    bandId: draft.bandId || '',
    speciesId: draft.speciesId || '',
    mutationName: draft.mutationName || '',
    gender: draft.gender ?? Gender.UNKNOWN,
    genderDeterminationMethod:
      draft.genderDeterminationMethod ?? GenderDeterminationMethod.NOT_SEXED,
    status: draft.status ?? BirdStatus.AVAILABLE,
    sellPrice: draft.sellPrice != null ? String(draft.sellPrice) : '',
    hatchingDate: dateOnly(draft.hatchingDate),
    bandingDate: dateOnly(draft.bandingDate),
    cageNumber: draft.cageNumber || '',
    provenance: {
      type: draft.provenance?.type || 'BOUGHT',
      fatherId: draft.provenance?.fatherId || '',
      motherId: draft.provenance?.motherId || '',
      sellerName: draft.provenance?.sellerName || '',
      sellerContact: draft.provenance?.sellerContact || '',
      sellerAddress: draft.provenance?.sellerAddress || '',
      dateAcquired: dateOnly(draft.provenance?.dateAcquired),
      price: draft.provenance?.price != null ? String(draft.provenance.price) : '',
      notes: draft.provenance?.notes || '',
      supplierId: draft.provenance?.supplierId || '',
      paymentMethod:
        (draft.provenance?.paymentMethod as PaymentMethod) || PaymentMethod.CASH,
      invoiceNumber: draft.provenance?.invoiceNumber || '',
      recordPurchase: Boolean(
        draft.provenance?.price && draft.provenance.price > 0,
      ),
    },
    phenotype: draft.phenotype || '',
    genotype: draft.genotype || '',
    notes: '',
  };
}

type FormData = {
  bandId: string;
  speciesId: string;
  mutationName: string;
  gender: Gender;
  genderDeterminationMethod: GenderDeterminationMethod;
  status: BirdStatus;
  sellPrice: string;
  hatchingDate: string;
  bandingDate: string;
  cageNumber: string;
  provenance: {
    type: 'BRED' | 'BOUGHT' | 'OTHER';
    fatherId: string;
    motherId: string;
    sellerName: string;
    sellerContact: string;
    sellerAddress: string;
    dateAcquired: string;
    price: string;
    notes: string;
    supplierId: string;
    paymentMethod: PaymentMethod;
    invoiceNumber: string;
    /** When true (BOUGHT), create a Purchases ledger entry with the bird */
    recordPurchase: boolean;
  };
  phenotype: string;
  genotype: string;
  notes: string;
};

const PROVENANCE_TAB_INDEX = 3;

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

export default function AddBirdModal({
  isOpen,
  onClose,
  onAdd,
  initialDraft = null,
}: AddBirdModalProps) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [species, setSpecies] = useState<Species[]>([]);
  const [loading, setLoading] = useState(false);
  const [pendingGenotypes, setPendingGenotypes] = useState<PendingGenotype[]>([]);
  const [geneticsSummary, setGeneticsSummary] = useState<GeneticsSummary>({
    mutationName: '',
    phenotype: '',
    genotype: '',
  });
  const [formData, setFormData] = useState<FormData>(emptyFormData);
  const skipSpeciesResetRef = useRef(false);
  const previousSpeciesIdRef = useRef(formData.speciesId);

  useEffect(() => {
    if (!isOpen) return;
    loadSpecies();
    if (initialDraft) {
      skipSpeciesResetRef.current = true;
      const next = draftToFormData(initialDraft);
      previousSpeciesIdRef.current = next.speciesId;
      setFormData(next);
      setGeneticsSummary({
        mutationName: initialDraft.mutationName || '',
        phenotype: initialDraft.phenotype || '',
        genotype: initialDraft.genotype || '',
      });
      setPendingGenotypes([]);
      setSelectedTab(0);
    } else {
      skipSpeciesResetRef.current = false;
      previousSpeciesIdRef.current = '';
      setFormData(emptyFormData());
      setGeneticsSummary({ mutationName: '', phenotype: '', genotype: '' });
      setPendingGenotypes([]);
      setSelectedTab(0);
    }
  }, [isOpen, initialDraft]);

  useEffect(() => {
    if (previousSpeciesIdRef.current === formData.speciesId) return;
    previousSpeciesIdRef.current = formData.speciesId;
    if (skipSpeciesResetRef.current) {
      skipSpeciesResetRef.current = false;
      return;
    }
    setPendingGenotypes([]);
    setGeneticsSummary({ mutationName: '', phenotype: '', genotype: '' });
    setFormData((prev) => ({
      ...prev,
      mutationName: '',
      phenotype: '',
      genotype: '',
    }));
  }, [formData.speciesId]);

  const handleGenotypesChange = useCallback(
    (genotypes: PendingGenotype[], summary: GeneticsSummary) => {
      setPendingGenotypes(genotypes);
      setGeneticsSummary(summary);
      setFormData((prev) => {
        const nextMutation = summary.mutationName || prev.mutationName;
        const nextPhenotype = summary.phenotype || prev.phenotype;
        const nextGenotype = summary.genotype || prev.genotype;
        if (
          nextMutation === prev.mutationName &&
          nextPhenotype === prev.phenotype &&
          nextGenotype === prev.genotype
        ) {
          return prev;
        }
        return {
          ...prev,
          mutationName: nextMutation,
          phenotype: nextPhenotype,
          genotype: nextGenotype,
        };
      });
    },
    [],
  );

  const loadSpecies = async () => {
    try {
      const speciesData = await getSpecies();
      setSpecies(
        [...speciesData].sort((a, b) =>
          a.commonName.localeCompare(b.commonName, undefined, { sensitivity: 'base' }),
        ),
      );
    } catch (err) {
      console.error('Error loading species:', err);
      toast.error('Failed to load species data');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name.startsWith('provenance.')) {
      const provenanceField = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        provenance: {
          ...prev.provenance,
          [provenanceField]: value
        }
      }));
    } else if (name === 'gender') {
      setFormData(prev => ({ ...prev, [name]: value as Gender }));
    } else if (name === 'genderDeterminationMethod') {
      setFormData(prev => ({ ...prev, [name]: value as GenderDeterminationMethod }));
    } else if (name === 'status') {
      setFormData(prev => ({ ...prev, [name]: value as BirdStatus }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.bandId || !formData.speciesId) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (!formData.cageNumber?.trim()) {
      toast.error('Please enter a cage number (Housing tab)');
      setSelectedTab(2);
      return;
    }

    if (formData.provenance.type === 'BOUGHT' && formData.provenance.recordPurchase) {
      const price = parseFloat(formData.provenance.price);
      if (!formData.provenance.sellerName.trim()) {
        toast.error('Enter the seller name to create the purchase entry');
        setSelectedTab(PROVENANCE_TAB_INDEX);
        return;
      }
      if (!formData.provenance.dateAcquired) {
        toast.error('Enter the purchase date');
        setSelectedTab(PROVENANCE_TAB_INDEX);
        return;
      }
      if (!formData.provenance.price || Number.isNaN(price) || price <= 0) {
        toast.error('Enter a purchase price greater than zero');
        setSelectedTab(PROVENANCE_TAB_INDEX);
        return;
      }
    }

    try {
      setLoading(true);
      
      // Helper function to safely convert date strings to ISO format
      const safeISODate = (dateString: string): string => {
        if (!dateString) {
          return new Date().toISOString();
        }
        const date = new Date(dateString);
        if (isNaN(date.getTime())) {
          return new Date().toISOString();
        }
        return date.toISOString();
      };
      
      // CreateBirdDto on the API does not accept documents / nested notes /
      // healthTests (forbidNonWhitelisted in production). Keep initial notes in
      // provenance.notes instead.
      const provenanceNotes = [formData.provenance.notes, formData.notes]
        .map((n) => n?.trim())
        .filter(Boolean)
        .join('\n\n');

      const shouldRecordPurchase =
        formData.provenance.type === 'BOUGHT' && formData.provenance.recordPurchase;
      const purchasePrice = shouldRecordPurchase
        ? parseFloat(formData.provenance.price)
        : formData.provenance.price
          ? parseFloat(formData.provenance.price)
          : undefined;

      const createBirdData: CreateBirdDto = {
        bandId: formData.bandId,
        speciesId: formData.speciesId,
        mutationName:
          geneticsSummary.mutationName || formData.mutationName || undefined,
        gender: formData.gender,
        genderDeterminationMethod: formData.genderDeterminationMethod,
        status: formData.status,
        sellPrice: formData.sellPrice ? parseFloat(formData.sellPrice) : undefined,
        hatchingDate: safeISODate(formData.hatchingDate),
        bandingDate: safeISODate(formData.bandingDate),
        cageNumber: formData.cageNumber,
        provenance: {
          type: formData.provenance.type,
          fatherId: formData.provenance.fatherId || undefined,
          motherId: formData.provenance.motherId || undefined,
          sellerName: formData.provenance.sellerName || undefined,
          sellerContact: formData.provenance.sellerContact || undefined,
          sellerAddress: formData.provenance.sellerAddress || undefined,
          dateAcquired: formData.provenance.dateAcquired
            ? safeISODate(formData.provenance.dateAcquired)
            : undefined,
          // Only send price when recording purchase so the API creates the ledger entry
          price:
            shouldRecordPurchase && purchasePrice && purchasePrice > 0
              ? purchasePrice
              : undefined,
          notes: provenanceNotes || undefined,
          supplierId: formData.provenance.supplierId || undefined,
          paymentMethod: shouldRecordPurchase
            ? formData.provenance.paymentMethod
            : undefined,
          invoiceNumber: formData.provenance.invoiceNumber || undefined,
        },
        phenotype: geneticsSummary.phenotype || formData.phenotype || undefined,
        genotype: geneticsSummary.genotype || formData.genotype || undefined,
        genotypes: pendingGenotypes.length ? pendingGenotypes : undefined,
        parentIds: [],
        siblingIds: [],
        descendantIds: [],
        photos: [],
      };

      await onAdd(createBirdData);
      
      // Reset form
      setPendingGenotypes([]);
      setGeneticsSummary({ mutationName: '', phenotype: '', genotype: '' });
      setFormData(emptyFormData());
      setSelectedTab(0);
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(getApiErrorMessage(err, 'Failed to add bird'));
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    {
      name: 'Basic Info',
      icon: InformationCircleIcon,
      content: <BasicInfoTab formData={formData} handleChange={handleChange} species={species} />
    },
    {
      name: 'Identity',
      icon: IdentificationIcon,
      content: <IdentityTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Housing',
      icon: HomeIcon,
      content: <HousingTab formData={formData} handleChange={handleChange} />
    },
    {
      name: 'Provenance',
      icon: HeartIcon,
      content: (
        <ProvenanceTab
          formData={formData}
          handleChange={handleChange}
          onProvenancePatch={(patch) =>
            setFormData((prev) => ({
              ...prev,
              provenance: { ...prev.provenance, ...patch },
            }))
          }
        />
      ),
    },
    {
      name: 'Genetics',
      icon: BeakerIcon,
      content: (
        <GeneticsTab
          formData={formData}
          handleChange={handleChange}
          pendingGenotypes={pendingGenotypes}
          onGenotypesChange={handleGenotypesChange}
        />
      ),
    },
  ];

  const boughtPurchaseReady =
    formData.provenance.type !== 'BOUGHT' ||
    !formData.provenance.recordPurchase ||
    (isNonEmptyString(formData.provenance.sellerName) &&
      isNonEmptyString(formData.provenance.dateAcquired) &&
      isNonEmptyString(formData.provenance.price) &&
      parseFloat(formData.provenance.price) > 0);

  const canSubmit =
    isNonEmptyString(formData.bandId) &&
    isNonEmptyString(formData.speciesId) &&
    isNonEmptyString(formData.cageNumber) &&
    isNonEmptyString(formData.hatchingDate) &&
    isNonEmptyString(formData.bandingDate) &&
    boughtPurchaseReady;

  return (
    <BrandModal open={isOpen} onClose={onClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader title="Add New Bird" onClose={onClose} />
      <BrandModalBody>
        <form onSubmit={handleSubmit} className="w-full">
          <div className="w-full">
            <Tab.Group selectedIndex={selectedTab} onChange={setSelectedTab}>
              <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-6">
                {categories.map((category) => (
                  <Tab
                    key={category.name}
                    className={({ selected }) =>
                      classNames(
                        'w-full rounded-lg py-2.5 px-3 text-sm font-medium leading-5 flex items-center justify-center space-x-2',
                        'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2',
                        selected
                          ? 'bg-white shadow text-cyan-700 border border-indigo-200'
                          : 'text-slate-600 hover:bg-white/[0.8] hover:text-gray-800'
                      )
                    }
                  >
                    <category.icon className="h-4 w-4" />
                    <span className="hidden sm:block">{category.name}</span>
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels className="mt-2">
                {categories.map((category, idx) => (
                  <Tab.Panel
                    key={idx}
                    className={classNames(
                      'rounded-xl bg-white p-6 shadow-sm border min-h-[400px]',
                      'ring-white ring-opacity-60 ring-offset-2 ring-offset-indigo-400 focus:outline-none focus:ring-2'
                    )}
                  >
                    {category.content}
                  </Tab.Panel>
                ))}
              </Tab.Panels>
            </Tab.Group>
          </div>

          <TabbedFormActions
            onCancel={onClose}
            tabCount={categories.length}
            selectedTab={selectedTab}
            onTabChange={setSelectedTab}
            canSubmit={canSubmit}
            submitLabel="Add Bird"
            loading={loading}
            loadingLabel="Adding..."
          />
        </form>
      </BrandModalBody>
    </BrandModal>
  );
}


// Tab Components
interface TabProps {
  formData: FormData;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
  species?: Species[];
}

function BasicInfoTab({ formData, handleChange, species }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Basic Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="bandId" className="block text-sm font-medium text-slate-700">
              Band ID <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="bandId"
              id="bandId"
              value={formData.bandId}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="speciesId" className="block text-sm font-medium text-slate-700">
              Species <span className="text-red-500">*</span>
            </label>
            <SpeciesCombobox
              id="speciesId"
              species={species || []}
              value={formData.speciesId}
              onChange={(speciesId) =>
                handleChange({
                  target: { name: 'speciesId', value: speciesId },
                } as React.ChangeEvent<HTMLSelectElement>)
              }
              required
              placeholder="Type to search species…"
            />
          </div>

          <div>
            <label htmlFor="gender" className="block text-sm font-medium text-slate-700">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              name="gender"
              id="gender"
              value={formData.gender}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={Gender.UNKNOWN}>Unknown</option>
              <option value={Gender.MALE}>Male</option>
              <option value={Gender.FEMALE}>Female</option>
            </select>
          </div>

          <div>
            <label htmlFor="genderDeterminationMethod" className="block text-sm font-medium text-slate-700">
              Gender Determination Method <span className="text-red-500">*</span>
            </label>
            <select
              name="genderDeterminationMethod"
              id="genderDeterminationMethod"
              value={formData.genderDeterminationMethod}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={GenderDeterminationMethod.NOT_SEXED}>Not Sexed</option>
              <option value={GenderDeterminationMethod.VISUALLY_SEXED}>Visually Sexed</option>
              <option value={GenderDeterminationMethod.DNA_SEXING}>DNA Sexing</option>
              <option value={GenderDeterminationMethod.SURGICAL_SEXING}>Surgical Sexing</option>
            </select>
          </div>

          <div>
            <label htmlFor="status" className="block text-sm font-medium text-slate-700">
              Status <span className="text-red-500">*</span>
            </label>
            <select
              name="status"
              id="status"
              value={formData.status}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value={BirdStatus.AVAILABLE}>Available</option>
              <option value={BirdStatus.PAIRED}>Paired</option>
              <option value={BirdStatus.FOR_SALE}>For Sale</option>
              <option value={BirdStatus.DECEASED}>Deceased</option>
              <option value={BirdStatus.LOST}>Lost</option>
              <option value={BirdStatus.DONATED}>Donated</option>
            </select>
          </div>

          {formData.status === BirdStatus.FOR_SALE && (
            <div>
              <label htmlFor="sellPrice" className="block text-sm font-medium text-slate-700">
                Sale Price (ZAR) <span className="text-red-500">*</span>
              </label>
              <AmountInput
                id="sellPrice"
                name="sellPrice"
                value={formData.sellPrice}
                onChange={(v) =>
                  handleChange({
                    target: { name: 'sellPrice', value: v != null ? String(v) : '' },
                  } as React.ChangeEvent<HTMLInputElement>)
                }
                required
                min={0}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function IdentityTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Identity & Dates</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="hatchingDate" className="block text-sm font-medium text-slate-700">
              Hatching Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="hatchingDate"
              id="hatchingDate"
              value={formData.hatchingDate}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>

          <div>
            <label htmlFor="bandingDate" className="block text-sm font-medium text-slate-700">
              Banding Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="bandingDate"
              id="bandingDate"
              value={formData.bandingDate}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            />
          </div>
        </div>

        <div className="mt-6">
          <label htmlFor="notes" className="block text-sm font-medium text-slate-700">
            Initial Notes
          </label>
          <textarea
            name="notes"
            id="notes"
            value={formData.notes}
            onChange={handleChange}
            rows={4}
            className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
            placeholder="Add any initial notes about this bird..."
          />
        </div>
      </div>
    </div>
  );
}

function HousingTab({ formData, handleChange }: TabProps) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Housing Information</h3>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <div>
            <label htmlFor="cageNumber" className="block text-sm font-medium text-slate-700">
              Cage Number <span className="text-red-500">*</span>
            </label>
            <CageSelect
              id="cageNumber"
              name="cageNumber"
              value={formData.cageNumber}
              onChange={(cageNumber) =>
                handleChange({ target: { name: 'cageNumber', value: cageNumber } } as React.ChangeEvent<HTMLInputElement>)
              }
              required
              placeholder="Search or add a cage number…"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ProvenanceTab({
  formData,
  handleChange,
  onProvenancePatch,
}: TabProps & {
  onProvenancePatch: (patch: Partial<FormData['provenance']>) => void;
}) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await suppliersApi.getSuppliers({ limit: 200 });
        if (!cancelled) {
          setSuppliers(
            [...(res.suppliers || [])].sort((a, b) =>
              a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }),
            ),
          );
        }
      } catch {
        // Suppliers are optional — manual seller fields still work
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSupplierChange = (supplierId: string) => {
    if (!supplierId) {
      onProvenancePatch({ supplierId: '' });
      return;
    }
    const supplier = suppliers.find((s) => s.id === supplierId);
    if (!supplier) {
      onProvenancePatch({ supplierId });
      return;
    }
    onProvenancePatch({
      supplierId,
      sellerName: supplier.name,
      sellerContact: supplier.phone || supplier.email || '',
      sellerAddress: supplier.fullAddress || '',
    });
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-4">Provenance & Origin</h3>
        
        <div className="space-y-6">
          <div>
            <label htmlFor="provenance.type" className="block text-sm font-medium text-slate-700">
              Provenance Type <span className="text-red-500">*</span>
            </label>
            <select
              name="provenance.type"
              id="provenance.type"
              value={formData.provenance.type}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              required
            >
              <option value="BRED">Bred</option>
              <option value="BOUGHT">Bought</option>
              <option value="OTHER">Other</option>
            </select>
          </div>

          {formData.provenance.type === 'BRED' && (
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="text-md font-medium text-green-900 mb-3">Breeding Information</h4>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="provenance.fatherId" className="block text-sm font-medium text-slate-700">
                    Father Band ID
                  </label>
                  <input
                    type="text"
                    name="provenance.fatherId"
                    id="provenance.fatherId"
                    value={formData.provenance.fatherId}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="e.g., M123"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.motherId" className="block text-sm font-medium text-slate-700">
                    Mother Band ID
                  </label>
                  <input
                    type="text"
                    name="provenance.motherId"
                    id="provenance.motherId"
                    value={formData.provenance.motherId}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="e.g., F456"
                  />
                </div>
              </div>
            </div>
          )}

          {formData.provenance.type === 'BOUGHT' && (
            <div className="bg-cyan-50 p-4 rounded-lg border border-blue-200 space-y-4">
              <div>
                <h4 className="text-md font-medium text-blue-900">Purchase Information</h4>
                <p className="mt-1 text-sm text-blue-800">
                  Filling seller and price creates a purchase entry under Purchases when you add the bird.
                </p>
              </div>

              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500"
                  checked={formData.provenance.recordPurchase}
                  onChange={(e) =>
                    onProvenancePatch({ recordPurchase: e.target.checked })
                  }
                />
                <span className="text-sm text-slate-700">
                  <span className="font-medium">Create purchase entry</span>
                  <span className="block text-slate-500">
                    Records the buy in finances (invoice, payment, and inventory cost).
                  </span>
                </span>
              </label>

              {suppliers.length > 0 && (
                <div>
                  <label htmlFor="provenance.supplierId" className="block text-sm font-medium text-slate-700">
                    Existing supplier (optional)
                  </label>
                  <select
                    id="provenance.supplierId"
                    value={formData.provenance.supplierId}
                    onChange={(e) => handleSupplierChange(e.target.value)}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option value="">Enter seller manually…</option>
                    {suppliers.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                        {s.code ? ` (${s.code})` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="provenance.sellerName" className="block text-sm font-medium text-slate-700">
                    Seller Name
                    {formData.provenance.recordPurchase && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  <input
                    type="text"
                    name="provenance.sellerName"
                    id="provenance.sellerName"
                    value={formData.provenance.sellerName}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    required={formData.provenance.recordPurchase}
                  />
                </div>

                <div>
                  <label htmlFor="provenance.sellerContact" className="block text-sm font-medium text-slate-700">
                    Seller Contact
                  </label>
                  <input
                    type="text"
                    name="provenance.sellerContact"
                    id="provenance.sellerContact"
                    value={formData.provenance.sellerContact}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    placeholder="Phone or email"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="provenance.sellerAddress" className="block text-sm font-medium text-slate-700">
                    Seller Address
                  </label>
                  <input
                    type="text"
                    name="provenance.sellerAddress"
                    id="provenance.sellerAddress"
                    value={formData.provenance.sellerAddress}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>

                <div>
                  <label htmlFor="provenance.dateAcquired" className="block text-sm font-medium text-slate-700">
                    Purchase Date
                    {formData.provenance.recordPurchase && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  <input
                    type="date"
                    name="provenance.dateAcquired"
                    id="provenance.dateAcquired"
                    value={formData.provenance.dateAcquired}
                    onChange={handleChange}
                    className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                    required={formData.provenance.recordPurchase}
                  />
                </div>

                <div>
                  <label htmlFor="provenance.price" className="block text-sm font-medium text-slate-700">
                    Purchase Price (ZAR)
                    {formData.provenance.recordPurchase && (
                      <span className="text-red-500"> *</span>
                    )}
                  </label>
                  <AmountInput
                    id="provenance.price"
                    name="provenance.price"
                    value={formData.provenance.price}
                    onChange={(v) =>
                      handleChange({
                        target: { name: 'provenance.price', value: v != null ? String(v) : '' },
                      } as React.ChangeEvent<HTMLInputElement>)
                    }
                    required={formData.provenance.recordPurchase}
                    min={0}
                  />
                </div>

                {formData.provenance.recordPurchase && (
                  <>
                    <div>
                      <label htmlFor="provenance.paymentMethod" className="block text-sm font-medium text-slate-700">
                        Payment Method
                      </label>
                      <select
                        name="provenance.paymentMethod"
                        id="provenance.paymentMethod"
                        value={formData.provenance.paymentMethod}
                        onChange={handleChange}
                        className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                      >
                        {Object.values(PaymentMethod).map((method) => (
                          <option key={method} value={method}>
                            {getPaymentMethodDisplay(method)}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="provenance.invoiceNumber" className="block text-sm font-medium text-slate-700">
                        Seller Invoice # (optional)
                      </label>
                      <input
                        type="text"
                        name="provenance.invoiceNumber"
                        id="provenance.invoiceNumber"
                        value={formData.provenance.invoiceNumber}
                        onChange={handleChange}
                        className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
                        placeholder="External invoice reference"
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <div>
            <label htmlFor="provenance.notes" className="block text-sm font-medium text-slate-700">
              Provenance Notes
            </label>
            <textarea
              name="provenance.notes"
              id="provenance.notes"
              value={formData.provenance.notes}
              onChange={handleChange}
              rows={4}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Additional notes about the bird's origin, breeding history, or acquisition details..."
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function GeneticsTab({
  formData,
  handleChange,
  pendingGenotypes,
  onGenotypesChange,
}: TabProps & {
  pendingGenotypes: PendingGenotype[];
  onGenotypesChange: (genotypes: PendingGenotype[], summary: GeneticsSummary) => void;
}) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-slate-900 mb-2">Genetic Information</h3>
        <p className="text-sm text-slate-500 mb-4">
          Record structured genotypes from the species mutation catalog. Summary fields below are
          filled automatically and can still be edited if needed.
        </p>

        <div className="rounded-lg border border-indigo-200 bg-indigo-50/30 p-4 mb-6">
          <BirdGenotypeEditor
            speciesId={formData.speciesId || undefined}
            gender={formData.gender}
            value={pendingGenotypes}
            autoSave={false}
            showImportLegacy={false}
            onChange={onGenotypesChange}
          />
        </div>

        <div className="space-y-6">
          <div>
            <label htmlFor="mutationName" className="block text-sm font-medium text-slate-700">
              Mutation summary
            </label>
            <input
              type="text"
              name="mutationName"
              id="mutationName"
              value={formData.mutationName}
              onChange={handleChange}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Filled from structured genotypes, or enter manually"
            />
          </div>

          <div>
            <label htmlFor="phenotype" className="block text-sm font-medium text-slate-700">
              Phenotype
            </label>
            <textarea
              name="phenotype"
              id="phenotype"
              value={formData.phenotype}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Visible characteristics — usually derived from genotypes"
            />
          </div>

          <div>
            <label htmlFor="genotype" className="block text-sm font-medium text-slate-700">
              Genotype text
            </label>
            <textarea
              name="genotype"
              id="genotype"
              value={formData.genotype}
              onChange={handleChange}
              rows={3}
              className="mt-1 block w-full input border-gray-300 shadow-sm sm:text-sm"
              placeholder="Legacy genotype text — usually derived from structured loci"
            />
          </div>
        </div>
      </div>
    </div>
  );
} 